<template>
  <b-container fluid class="max-width">
    <title-back-button
      :title="trans('courses.certificate.page_title')"
      :show-breadcrumbs="true"
      :current-page="trans('courses.certificate.page_title')"
      :prev-page="back.link"
      :prev-page-link="parseNav(back)"
    />
    <div class="row">
      <div class="col-12 mb-4" v-if="existWebClasses && webClassesNotCompleted.length > 0">
        <notice-card
          :illustration="'/images/certificate/webclasses.svg'"
          :title="trans('courses.certificate.notice_card.webclasses.title')"
          :description="trans('courses.certificate.notice_card.webclasses.description')"
          :buttonText="trans('courses.certificate.notice_card.webclasses.button_text')"
          :route="parseNav(back)"
        />
      </div>

      <div class="col-12 mb-4" v-if="existWebClasses && webClassesNotCompleted.length == 0">
        <notice-card
          :illustration="'/images/certificate/journal.svg'"
          :title="trans('courses.certificate.notice_card.journal.title')"
          :description="trans('courses.certificate.notice_card.journal.description')"
          :buttonText="trans('courses.certificate.notice_card.journal.button_text')"
          @ctaClicked="goToJournal"
        />
      </div>

      <div class="col-12 col-lg-10 offset-lg-1 content">
        <downloadable-content :service-name="serviceName" :is-augmented="isAugmented">
          <div class="scrollable-container" id="wrapper">
            <b-card>
              <div class="row">
                <div class="col-12">
                  <div class="row">
                    <div class="col-4 col-md-3">
                      <b-img src="/images/logo_lifeed.png" class="logo-lifeed" />
                    </div>
                    <div class="col-8 col-md-9 text-right" v-if="logo">
                      <b-img
                        class="img-fluid logo-company"
                        :src="logo + '?cacheblock=yes'"
                        crossorigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="text-center">
                    <h3 class="h5 pt-2 mb-1">{{ userName }}</h3>
                    <small class="mb-2">{{ trans("courses.certificate.complete") }}</small>
                    <h5 class="font-weight-bold mt-4 mb-1 text-secondary">{{ serviceName }}</h5>
                    <p class="font-weight-bold">
                      {{ trans("courses.certificate.training_program") }}
                    </p>
                    <div class="row d-flex justify-content-center">
                      <b-img
                        class="illustration img-fluid"
                        :src="illustration + '?cacheblock=yes'"
                        crossorigin="anonymous"
                      />
                    </div>
                  </div>
                </div>

                <div class="col-12 mt-3">
                  <small class="font-weight-bold mb-2 d-flex justify-content-center">{{
                    trans("courses.certificate.skills")
                  }}</small>
                  <div class="row justify-content-center mt-2">
                    <div
                      class="col-12 col-md-auto mb-2 px-2"
                      v-for="competence in competences"
                      :key="competence.id"
                    >
                      <certificate-competence :name="competence.title" :image="competence.image" />
                    </div>
                  </div>
                </div>

                <div class="col-12 mt-4" v-if="existWebClasses && webClassesCompleted.length > 0">
                  <small class="font-weight-bold mb-2 d-flex justify-content-center">{{
                    trans("courses.certificate.webclasses")
                  }}</small>
                  <div class="row justify-content-center mt-2">
                    <ul class="list-unstyled text-center">
                      <li
                        class="mb-0 px-2"
                        v-for="(webClass, index) in webClassesCompleted"
                        :key="index"
                      >
                        "{{ webClass.title }}"
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="col-12 d-flex flex-row justify-content-end">
                  <div class="row">
                    <div class="col-12 col-md-auto text-right">
                      <b-img
                        class="img-signature img-fluid"
                        src="/images/certificate/riccarda_signature.png"
                      />
                      <small class="font-weight-bold p-2 d-block mb-0">Riccarda Zezza</small>
                      <small class="d-block mb-0">{{ trans("courses.certificate.ceo") }}</small>
                    </div>
                    <div class="col-12 col-md-auto align-self-end text-right">
                      <b-img class="img-medal" src="/images/certificate/medal.svg" />
                    </div>
                  </div>
                </div>
              </div>
            </b-card>
          </div>
        </downloadable-content>
      </div>

      <div class="col-12 col-lg-10 offset-lg-1 mt-4 mb-5">
        <p class="font-weight-bold">{{ trans("courses.certificate.how_to_share.title") }}</p>
        <ol class="pl-3 pb-5 mb-5">
          <li>{{ trans("courses.certificate.how_to_share.1") }}</li>
          <li>{{ trans("courses.certificate.how_to_share.2") }}</li>
          <li>{{ trans("courses.certificate.how_to_share.3") }}</li>
          <li>{{ trans("courses.certificate.how_to_share.4", { master_name: serviceName }) }}</li>
          <li>{{ trans("courses.certificate.how_to_share.5") }}</li>
          <li>{{ trans("courses.certificate.how_to_share.6") }}</li>
        </ol>
      </div>
    </div>
    <certificate-privacy-modal
      v-if="showPrivacyModal"
      @submitModal="certificateActivity(privacyCheck)"
      @cancelModal="disableSharing()"
    />
  </b-container>
</template>

<script>
import DownloadableContent from "./DownloadableContent.vue";
import CertificateCompetence from "../atoms/CertificateCompetence";
import NoticeCard from "../molecules/cards/NoticeCard";
import navMixin from "../mixins/navMixin";
import Icon from "../atoms/Icon";
import CertificatePrivacyModal from "../molecules/CertificatePrivacyModal";
import PrivacyAlert from "../molecules/PrivacyAlert";
import TitleBackButton from "../molecules/TitleBackButton";

export default {
  name: "Certificate",
  components: {
    DownloadableContent,
    CertificateCompetence,
    NoticeCard,
    Icon,
    CertificatePrivacyModal,
    PrivacyAlert,
    TitleBackButton,
  },
  props: {
    userName: String,
    serviceName: String,
    serviceType: String,
    illustration: String,
    competences: Array | Object,
    logo: String,
    back: Object,
    webClasses: Array,
    isAugmented: Boolean,
  },
  data() {
    return {
      token: null,
      privacyCheck: false,
      showPrivacyModal: false,
      get privacyCookie() {
        return localStorage.getItem("shareCertificate") || 0;
      },
    };
  },
  methods: {
    download() {
      trackEvent("downloadCertificate", {
        serviceName: this.serviceName,
        isAugmented: this.isAugmented,
      });
    },
    goToJournal() {
      this.$router.push({ name: "journal-softskills" });
    },
    certificateActivity(checked) {
      const data = {
        serviceId: this.$route.params.id,
        serviceType: this.serviceType,
        logo: this.logo,
        userName: this.userName,
        serviceName: this.serviceName,
        illustration: this.illustration,
        competences: this.competences,
        webClasses: this.webClasses,
      };

      if (checked) {
        if (this.privacyCookie) {
          this.$axios
            .post(`/api/rest/certificate/store`, data)
            .then((res) => {
              this.getCertificateToken();
              this.showPrivacyModal = false;
              trackEvent("CertificateSharingEnabled", {
                serviceName: this.serviceName,
                serviceId: this.$route.params.id,
                serviceType: this.serviceType,
              });
            })
            .catch((err) => console.log(err));
        } else {
          this.showPrivacyModal = true;
        }
      } else {
        this.$axios
          .delete(`/api/rest/certificate/destroy/${this.$route.params.id}/${this.serviceType}`)
          .then((res) => {})
          .catch((err) => console.log(err));
      }
    },
    checkCertificateAvailable() {
      this.$axios
        .get(`/api/rest/certificate/token/${this.$route.params.id}/${this.serviceType}`)
        .then((res) => {
          if (res.status === 200) {
            this.privacyCheck = true;
            localStorage.setItem("shareCertificate", "1");
            this.token = res.data.certificate_token;
          }
        })
        .catch((err) => console.log(err));
    },
    getCertificateToken() {
      this.$axios
        .get(`/api/rest/certificate/token/${this.$route.params.id}/${this.serviceType}`)
        .then((res) => {
          this.token = res.data.certificate_token;
        })
        .catch((err) => console.log(err));
    },
    disableSharing() {
      this.privacyCheck = false;
      this.showPrivacyModal = false;
    },
  },
  computed: {
    existWebClasses: function () {
      return this.webClasses && this.webClasses.length > 0;
    },
    webClassesCompleted: function () {
      return this.webClasses.filter((wc) => wc.completed === true);
    },
    webClassesNotCompleted: function () {
      return this.webClasses.filter((wc) => wc.completed === false);
    },
    getUrl: function () {
      return `${process.env.CERTIFICATE_DOMAIN}${this.token}`;
    },
  },
  mixins: [navMixin],
  mounted() {
    this.checkCertificateAvailable();
  },
};
</script>

<style scoped lang="scss">
.content {
  #wrapper {
    border: 8px solid $color-orange;
    .logo-lifeed {
      height: 60px;
      width: 60px;
    }
    .logo-company {
      max-height: 40px;
    }
    &.scrollable-container {
      width: 100%;
      display: flex;
      justify-content: center;
      padding: 5px;
    }
    .card {
      width: 68.125rem;
    }
    h5 {
      @media (max-width: 768px) {
        line-height: 1.3;
      }
    }
    .illustration {
      width: 300px;
      height: 150px;
    }
    .img-signature {
      height: 60px;
      width: 250px;
    }
    .img-medal {
      height: 80px;
      width: 80px;
    }
  }
}
</style>
